import type { ReactNode } from 'react';
import type { MediaFilters, SortKey } from '@/utils/media';
import { regionOptions, sortOptions, yearOptions } from '@/utils/media';

interface FilterBarProps {
  detailed: boolean;
  filters: MediaFilters;
  sort: SortKey;
  availableGenres: string[];
  onFiltersChange: (next: MediaFilters) => void;
  onSortChange: (sort: SortKey) => void;
  onReset: () => void;
}

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? 'border-primary-500 bg-primary-500 text-background-50'
          : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1 text-sm font-medium text-foreground-500 sm:w-16">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterBar({
  detailed,
  filters,
  sort,
  availableGenres,
  onFiltersChange,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const toggleGenre = (genre: string) => {
    if (detailed) {
      const genres = filters.genres.includes(genre)
        ? filters.genres.filter((g) => g !== genre)
        : [...filters.genres, genre];
      onFiltersChange({ ...filters, genres });
    } else {
      const genres = filters.genres.includes(genre) ? [] : [genre];
      onFiltersChange({ ...filters, genres });
    }
  };

  const toggleRegion = (region: string) => {
    if (detailed) {
      const regions = filters.regions.includes(region)
        ? filters.regions.filter((r) => r !== region)
        : [...filters.regions, region];
      onFiltersChange({ ...filters, regions });
    } else {
      const regions = filters.regions.includes(region) ? [] : [region];
      onFiltersChange({ ...filters, regions });
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-2 md:px-5">
      <div className="divide-y divide-background-200">
        <Row label="类型">
          {!detailed && (
            <Chip active={filters.genres.length === 0} onClick={() => toggleGenre('')}>
              全部
            </Chip>
          )}
          {availableGenres.map((genre) => (
            <Chip key={genre} active={filters.genres.includes(genre)} onClick={() => toggleGenre(genre)}>
              {genre}
            </Chip>
          ))}
        </Row>

        <Row label="地区">
          {!detailed && (
            <Chip active={filters.regions.length === 0} onClick={() => toggleRegion('')}>
              全部
            </Chip>
          )}
          {regionOptions.map((region) => (
            <Chip key={region} active={filters.regions.includes(region)} onClick={() => toggleRegion(region)}>
              {region}
            </Chip>
          ))}
        </Row>

        <Row label="年份">
          {detailed ? (
            <>
              <select
                value={filters.yearFrom ?? ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    yearFrom: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="">开始年份</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <span className="self-center text-foreground-400">—</span>
              <select
                value={filters.yearTo ?? ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    yearTo: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="">结束年份</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              <Chip
                active={filters.yearFrom === null && filters.yearTo === null}
                onClick={() => onFiltersChange({ ...filters, yearFrom: null, yearTo: null })}
              >
                全部
              </Chip>
              {yearOptions.map((y) => (
                <Chip
                  key={y}
                  active={filters.yearFrom === y && filters.yearTo === y}
                  onClick={() =>
                    onFiltersChange({
                      ...filters,
                      yearFrom: filters.yearFrom === y ? null : y,
                      yearTo: filters.yearFrom === y ? null : y,
                    })
                  }
                >
                  {y}
                </Chip>
              ))}
            </>
          )}
        </Row>

        <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="shrink-0 text-sm font-medium text-foreground-500 sm:w-16">排序</span>
            <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onSortChange(opt.key)}
                  className={`rounded-full px-3 py-1 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    sort === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 self-start rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600 sm:self-auto cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}